import { computed, onUnmounted, ref, watch } from 'vue';
import { useAccountPreferences } from './account-preferences.js';
import { safeStorage } from './storage.js';
import {
  applyTheme,
  readThemePreference,
  resolveTheme,
  themeStorageKey,
  type ThemePreference,
} from './theme.js';

/** Owns the appearance preference for the lifetime of the app. */
export function useTheme() {
  const account = useAccountPreferences();
  const local = ref<ThemePreference>(account ? 'dark' : readThemePreference(safeStorage()));
  const preference = computed({
    get: () => (account ? account.state.value.settings.theme : local.value),
    set: (value: ThemePreference) => {
      if (account)
        void account.update({ type: 'settings', patch: { theme: value } }).catch(() => {});
      else local.value = value;
    },
  });
  const query = window.matchMedia?.('(prefers-color-scheme: light)');
  const meta = document.querySelector('meta[name="theme-color"]');
  const apply = () =>
    applyTheme(
      resolveTheme(preference.value, query?.matches ?? false),
      document.documentElement,
      meta,
    );
  const onChange = () => {
    if (preference.value === 'system') apply();
  };
  query?.addEventListener('change', onChange);
  watch(
    preference,
    (value) => {
      apply();
      try {
        if (!account) safeStorage()?.setItem(themeStorageKey, value);
      } catch {
        // Storage can be unavailable in private windows; the theme still applies for this session.
      }
    },
    { immediate: true },
  );
  onUnmounted(() => query?.removeEventListener('change', onChange));
  return { preference };
}
